import axios from 'axios';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ClipLoader from 'react-spinners/ClipLoader';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { BASE_URL } from '../../Services/Services';

const AccountSecurity = () => {
  const [activeSection, setActiveSection] = useState('password');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const { t } = useTranslation();

  const handleSectionClick = (section) => {
    setActiveSection(section);
  };

  const validatePassword = (newPassword, confirmPassword) => {
    const passwordCriteria = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$.!%*?&]{8,}$/;
    if (!passwordCriteria.test(newPassword)) {
      return "Password must be at least 8 characters long, include a letter & a number.";
    }
    if (newPassword !== confirmPassword) {
      return "New password and confirmation do not match.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    toast.dismiss();
    const validationError = validatePassword(newPassword, confirmPassword);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }
    setError('');

    toast.dismiss();
    const email = localStorage.getItem('email');
    const oldPassword = currentPassword;
    const url = `${BASE_URL}/addPassword?email=${email}&oldPassword=${oldPassword}&newPassword=${newPassword}&confirmPassword=${confirmPassword}`;

    if (newPassword === oldPassword) {
      toast.error('Please use a different password; it matches the existing one');
      return;
    }
    setLoading(true);

    try {
      const res = await axios.put(url, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      toast.dismiss();
      console.log('Response:', res?.data);
      toast.success('Password updated successfully!');

      
      setNewPassword('');
      setCurrentPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.dismiss();
      if (error?.response?.status === 400) {
        const errorMessage = error?.response?.data?.message || 'Bad Request. Please check the input.';
        toast.error(errorMessage);
      } else {
        setError('Error updating password. Please try again.');
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'password':
        return (
          <div className="section-content">
            {loading && (
              <div className="loading-overlay">
                <ClipLoader color="black" loading={loading} size={50} />
                <p>{t("Please wait, your password is being updated.")}</p>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              {/* Current Password */}
              <div className="password-input-container">
                <input
                  className="input-password"
                  type={currentPasswordVisible ? 'text' : 'password'}
                  placeholder={t('CurrentPassword')}
                  autoComplete="off"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setCurrentPasswordVisible(!currentPasswordVisible)}
                  className="eye-icon-btn"
                >
                  {currentPasswordVisible ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              {/* New Password */}
              <div className="password-input-container">
                <input
                  className="input-password"
                  type={newPasswordVisible ? 'text' : 'password'}
                  placeholder={t('NewPassword')}
                  autoComplete="off"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => {
                    setNewPasswordVisible(!newPasswordVisible);
                    setConfirmPasswordVisible(!confirmPasswordVisible);
                  }}
                  className="eye-icon-btn"
                >
                  {newPasswordVisible ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              <div className="password-input-container">
                <input
                  className="input-password"
                  type={confirmPasswordVisible ? 'text' : 'password'}
                  placeholder={t('ReTypeNewPassword')}
                  autoComplete="off"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              {error && <div className="error-message">{error}</div>}
              <button className="save-btn">{t('PasswordUpdate')}</button>
            </form>
          </div>
        );
      // Commented out sections for future use
      // case '2fa':
      //   return (
      //     <div className="section-content-2fa">
      //       <div className='label-2fa'>
      //         <input type="radio" name="2fa" value="mobile" />
      //         {t('MobileAppAuthenticator')}
      //         <p>{t('UseMobileApp')}</p>
      //       </div>
      //       <div className='label-2fa'>
      //         <input type="radio" name="2fa" value="email" />
      //         {t('Email')}
      //         <p>{t('ReceiveVerificationCode')}</p>
      //       </div>
      //       <button className="save-btn">{t('Continue')}</button>
      //     </div>
      //   );
      // case 'activity':
      //   return (
      //     <div className="section-content-activity">
      //       <ul className="activity-log">
      //         <li>Aug 16 - {t('AccountActivityLog')}</li>
      //         <li>Aug 15 - {t('AccountActivityLog')}</li>
      //         <li>Aug 14 - {t('AccountActivityLog')}</li>
      //         <li>Aug 13 - {t('AccountActivityLog')}</li>
      //       </ul>
      //     </div>
      //   );
      default:
        return null;
    }
  };

  return (
    <div className="section-content">
      <div className="tabs">
        <button
          className={activeSection === 'password' ? 'active' : ''}
          onClick={() => handleSectionClick('password')}
        >
          {t('PasswordUpdate')}
        </button>
        {/* Uncomment if needed in future */}
        {/* <button
          className={activeSection === '2fa' ? 'active' : ''}
          onClick={() => handleSectionClick('2fa')}
        >
          {t('TwoFASettings')}
        </button>
        <button
          className={activeSection === 'activity' ? 'active' : ''}
          onClick={() => handleSectionClick('activity')}
        >
          {t('AccountActivityLog')}
        </button> */}
      </div>
      {renderContent()}
    </div>
  );
};

export default AccountSecurity;
